import { Check, Copy, WrapText } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { ThemedToken } from "shiki/core";

import { highlight, resolveLanguage } from "~/lib/highlight";
import { ICON_STROKE } from "~/lib/icon";

const COPIED_MS = 1500;
const CODE_WRAP_KEY = "chat.codeblock.wrap";

function readCodeWrap(): boolean {
  try {
    return window.localStorage.getItem(CODE_WRAP_KEY) !== "false";
  } catch {
    return true;
  }
}

function writeCodeWrap(wrap: boolean): void {
  try {
    window.localStorage.setItem(CODE_WRAP_KEY, wrap ? "true" : "false");
  } catch {
    // Private mode or quota: the toggle still works for this block.
  }
}

const COMMENT_COLOR = "var(--chat-code-token-comment)";

function tokenStyle(token: ThemedToken): CSSProperties | undefined {
  const fontStyle = token.fontStyle ?? 0;
  if (!token.color && !fontStyle) return undefined;
  return {
    color: token.color,
    fontStyle: fontStyle & 1 || token.color === COMMENT_COLOR ? "italic" : undefined,
    fontWeight: fontStyle & 2 ? 600 : undefined,
  };
}

export function CodeBlock({ code, lang }: { code: string; lang: string | null }) {
  const language = resolveLanguage(lang);
  const [lines, setLines] = useState<ThemedToken[][] | null>(null);
  const [copied, setCopied] = useState(false);
  const [wrap, setWrap] = useState(readCodeWrap);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLines(null);
    if (!language) return;
    let live = true;
    highlight(code, language).then(
      (next) => live && setLines(next),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [code, language]);

  useEffect(() => () => clearTimeout(copiedTimer.current ?? undefined), []);

  const copy = () => {
    void navigator.clipboard?.writeText(code).then(() => {
      setCopied(true);
      clearTimeout(copiedTimer.current ?? undefined);
      copiedTimer.current = setTimeout(() => setCopied(false), COPIED_MS);
    });
  };

  const label = lang?.trim().toLowerCase() || null;
  return (
    <div className="chat-md-codeblock" data-language={language ?? undefined}>
      <div className="chat-md-codeblock-bar">
        <span>{label}</span>
        <button
          type="button"
          className="chat-md-codeblock-copy"
          onClick={() => {
            const next = !wrap;
            setWrap(next);
            writeCodeWrap(next);
          }}
          aria-label="Wrap lines"
          aria-pressed={wrap}
          title={wrap ? "Show original lines" : "Wrap lines"}
          style={{ marginLeft: "auto", color: wrap ? undefined : "var(--chat-fg-secondary)" }}
        >
          <WrapText size={14} strokeWidth={ICON_STROKE} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="chat-md-codeblock-copy"
          onClick={copy}
          aria-label={copied ? "Copied" : "Copy code"}
          title={copied ? "Copied" : "Copy code"}
        >
          {copied ? (
            <Check size={14} strokeWidth={ICON_STROKE} aria-hidden="true" />
          ) : (
            <Copy size={14} strokeWidth={ICON_STROKE} aria-hidden="true" />
          )}
        </button>
      </div>
      <pre
        data-wrap={wrap ? "true" : "false"}
        style={{
          whiteSpace: wrap ? "pre-wrap" : "pre",
          wordBreak: wrap ? "break-word" : "normal",
          overflowX: wrap ? "hidden" : "auto",
        }}
      >
        <code>
          {lines
            ? lines.map((line, i) => (
                <span key={i} className="chat-md-codeline">
                  {line.map((token, j) => (
                    <span key={j} style={tokenStyle(token)}>
                      {token.content}
                    </span>
                  ))}
                  {i < lines.length - 1 && "\n"}
                </span>
              ))
            : code}
        </code>
      </pre>
    </div>
  );
}
